import { DOCUMENT } from '@angular/common';
import { inject, Pipe, PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { v4 } from 'uuid';

@Pipe({ name: 'guideHtml' })
export class GuideHtmlPipe implements PipeTransform {
    private readonly sanitizer = inject(DomSanitizer);
    private readonly document = inject(DOCUMENT);

    transform(html: string): SafeHtml {
        const content = this.document.createElement('template');
        content.innerHTML = html;
        this.linkify(content.content);
        const frames: [string, string][] = [];

        for (const original of content.content.querySelectorAll('iframe')) {
            const frame = this.createFrame(original);
            if (!frame) {
                original.remove();
                continue;
            }
            const marker = `guide-frame-${v4()}`;
            original.replaceWith(this.document.createTextNode(marker));
            frames.push([marker, frame.outerHTML]);
        }

        // Sanitize ordinary HTML first; only the newly constructed frames are trusted.
        let sanitized = this.sanitizer.sanitize(SecurityContext.HTML, content.innerHTML) ?? '';
        for (const [marker, frame] of frames) {
            sanitized = sanitized.replace(marker, () => frame);
        }
        return this.sanitizer.bypassSecurityTrustHtml(sanitized);
    }

    private linkify(content: DocumentFragment): void {
        const walker = this.document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
        const nodes: Text[] = [];
        while (walker.nextNode()) {
            const node = walker.currentNode as Text;
            if (!node.parentElement?.closest('a, script, style, textarea, pre, code, iframe')) {
                nodes.push(node);
            }
        }

        for (const node of nodes) {
            const text = node.data;
            const fragment = this.document.createDocumentFragment();
            let offset = 0;
            for (const match of text.matchAll(/\b(?:https?:\/\/|www\.)[^\s<>"']+/gi)) {
                let label = match[0].replace(/[.,!?;:]+$/, '');
                for (const [open, close] of [['(', ')'], ['[', ']'], ['{', '}']]) {
                    while (label.endsWith(close) && label.split(close).length > label.split(open).length) {
                        label = label.slice(0, -1);
                    }
                }
                let url: URL;
                try {
                    url = new URL(/^www\./i.test(label) ? `https://${label}` : label);
                } catch {
                    continue;
                }

                fragment.append(this.document.createTextNode(text.slice(offset, match.index)));
                const link = this.document.createElement('a');
                link.href = url.href;
                link.textContent = label;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                fragment.append(link);
                offset = match.index + label.length;
            }
            if (offset) {
                fragment.append(this.document.createTextNode(text.slice(offset)));
                node.replaceWith(fragment);
            }
        }
    }

    private createFrame(original: HTMLIFrameElement): HTMLIFrameElement | null {
        let url: URL;
        try {
            url = new URL(original.getAttribute('src') ?? '');
        } catch {
            return null;
        }
        if (url.protocol !== 'https:' || url.origin === this.document.location.origin || url.username || url.password) {
            return null;
        }

        const frame = this.document.createElement('iframe');
        frame.src = url.href;
        frame.title = original.title || 'Video';
        frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
        frame.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('loading', 'lazy');
        frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        frame.style.maxWidth = '100%';
        frame.style.border = '0';
        for (const dimension of ['width', 'height']) {
            const value = original.getAttribute(dimension);
            frame.setAttribute(dimension, value && /^\d+%?$/.test(value) ? value : dimension === 'width' ? '560' : '315');
        }
        return frame;
    }
}
